import { useActiveProject } from "@/features/project/ActiveProject";
import { createQuotation, useQuotationSearchByCustomerId } from "@repo/api";
import { t } from "@repo/translation";
import { ModalSheet, SectionContainer, useCustomer, useToast } from "@repo/ui";
import {
  getErrorMessage,
  logError,
  trackAnalytics,
  useTypesafeActions,
} from "@repo/utils";
import { useState } from "react";

import {
  type AddQuotationState,
  type AddQuotationStateActions,
  AddQuotationStateDefault,
  addQuotationStateActions,
  addQuotationStateReducer,
} from "./AddQuotationReducer";
import { QuotationResultList } from "./QuotationResultList";
import { SearchCustomerQuotations } from "./SearchCustomerQuotations";

interface AddQuotationModalProps {
  isOpen: boolean;
  closeModal: () => void;
  showUnLinkedQuotationOverlayItems: boolean;
  associatedQuotations: string[];
}

export const AddQuotationModal = ({
  isOpen,
  closeModal,
  showUnLinkedQuotationOverlayItems,
  associatedQuotations,
}: AddQuotationModalProps) => {
  const { updateToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const { opportunity } = useActiveProject();
  const { customer } = useCustomer(opportunity?.id);
  const [state, actions] = useTypesafeActions<
    AddQuotationState,
    AddQuotationStateActions
  >(
    addQuotationStateReducer,
    { ...AddQuotationStateDefault, defaultPartyId: opportunity?.partyId },
    addQuotationStateActions
  );

  const {
    data: customerQuotations,
    isValidating: isLoadingCustomerQuotations,
  } = useQuotationSearchByCustomerId(
    state.customerInfo?.id || customer?.partyId
  );

  const showQuotations = !state.searchValue || state.customerInfo;

  const filteredUnlinkedQuotations = !showUnLinkedQuotationOverlayItems
    ? customerQuotations
    : customerQuotations?.filter(
        (quotation) => !associatedQuotations.includes(quotation.quotationId)
      );

  const onClickSave = async () => {
    setIsSaving(true);
    try {
      if (state.quotationNumbers && opportunity?.id !== undefined) {
        for (const quotationNumber of state.quotationNumbers) {
          await createQuotation(opportunity.id, quotationNumber)
            .then(() => {
              updateToast({
                id: quotationNumber,
                title: t("prompt.quotation_saved"),
              });
            })
            .catch((err) => {
              updateToast({
                id: quotationNumber,
                title: t("prompt.quotation_saved_failed"),
                message: getErrorMessage(err),
                type: "error",
              });
            });
        }
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsSaving(false);
      closeModal();
    }
  };

  return (
    <ModalSheet
      id="add-quotation-modal"
      title={t("action.add_quotations")}
      visible={isOpen}
      onClose={closeModal}
      onBack={
        state.customerInfo
          ? () => {
              actions.setCustomer(undefined);
            }
          : undefined
      }
      primaryButtonProps={{
        text:
          (state.quotationNumbers?.length ?? 0) > 0
            ? t("action.add_quotation_total", {
                total: state.quotationNumbers?.length,
              })
            : t("prompt.none_selected"),
        disabled:
          !state.quotationNumbers || state.quotationNumbers.length === 0,
        loading: isSaving,
        onClick: () => {
          onClickSave();
          trackAnalytics("add_quotation");
        },
      }}
    >
      <SectionContainer>
        <SearchCustomerQuotations state={state} actions={actions} />
        {showQuotations && (
          <QuotationResultList
            state={state}
            selectQuotations={actions.setQuotationNumbers}
            isLoading={isLoadingCustomerQuotations}
            customerQuotations={filteredUnlinkedQuotations}
          />
        )}
      </SectionContainer>
    </ModalSheet>
  );
};
